import { estadoDelPrestamo, cortesDePeriodo, siguienteCorte } from './prestamo';

// Cuándo le toca pagar a cada quien, y cuántos días lleva de atraso.
//
// Esto es distinto de «días sin pagar», que es lo que se mostraba antes y lo que
// llevó a un cliente a decir «pero si ya pagó». Un ejemplo real:
//
//   Préstamo del 5 de junio, mensual. Pagó puntual el 5 de julio.
//   Al 10 de agosto:
//     días sin pagar  = 36   ← suena a abandono
//     días de atraso  =  5   ← lo que de verdad pasa
//
// Las dos cifras son ciertas, pero solo una sirve para cobrar. Al lado de la
// palabra «atrasado», 36 hace pensar que el cliente desapareció.
//
// El calendario NO se cuenta desde el último pago sino desde el inicio del
// préstamo, que es donde se genera el interés: pagar tarde no corre la fecha
// del siguiente cobro. Se usan las mismas fechas que el cálculo del interés
// —`cortesDePeriodo`— para que ninguna pantalla contradiga a otra.

const DIA = 86400000;

// Contar días entre fechas tiene una trampa que cuesta un día entero de error.
//
// Las fechas guardadas ('2026-06-05') se leen como medianoche UTC, mientras que
// `new Date()` es la hora local. En Colombia, que va cinco horas detrás, una
// fecha UTC del 5 «es» el 4 a las siete de la tarde en local: normalizar con
// setHours() la corría un día y el atraso salía de 6 días en vez de 5.
//
// Se pasa cada fecha a un número de día entero: los cortes ya vienen en
// medianoche UTC, y de «hoy» se toma el día del calendario LOCAL, que es el que
// ve quien está cobrando.
const diaDeCorte = (d) => Math.floor(d.getTime() / DIA);
const diaDeHoy = (d) =>
  Math.floor(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DIA);

// «Hoy» como medianoche UTC del día local, para poder compararlo con los cortes.
const hoyComoCorte = (d) =>
  new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

// Un peso de tolerancia: los intereses se calculan con decimales y comparar
// contra cero exacto marca como moroso a quien pagó al céntimo.
const NADA = 1;

const DIAS_POR_PERIODO = { daily: 1, weekly: 7, biweekly: 15, monthly: 30 };

// ── El cobradiario se atrasa en CUOTAS, no en días ──
//
// En el modelo de Lenin el atraso se mide por el interés que quedó sin pagar
// en un corte. En cuota fija no hay cortes de interés: el total se pactó el
// primer día y lo que se debe es la resta. Preguntarle a este modelo por
// `interesPendiente` da una cifra que está por encima de cero casi toda la
// vida del préstamo —es la parte de interés del total que aún no se ha
// cubierto—, así que el camino de arriba marcaría a TODOS los cobradiarios
// como atrasados desde el segundo día, y con cientos de días de atraso.
//
// Aquí la pregunta es otra: cuántas cuotas debería haber entregado a hoy, y
// cuántas entregó. La diferencia es el atraso, y se cuenta en cuotas.
//
// Una cuota pendiente es la de hoy —normal, todavía no ha pasado el cobrador—.
// Dos o más ya es atraso de verdad.
function cobroCuotaFija(prestamo, estado, referencia, hoyNum) {
  const diasPeriodo = DIAS_POR_PERIODO[prestamo.frequency] || 30;
  const cuota = estado.cuota || 0;

  if (estado.saldado || cuota <= 0) {
    return {
      estado, situacion: 'al_dia', desdeCuandoDebe: null, diasAtraso: 0,
      proximoCobro: null, diasParaProximo: null, aCobrar: 0,
    };
  }

  const atrasadas = estado.cuotasAtrasadas || 0;

  // Cuándo venció la más vieja que sigue sin pagar. Se cuenta desde el inicio
  // del préstamo, igual que el otro modelo: pagar tarde no corre la fecha.
  const inicio = new Date(prestamo.start_date);
  const venceCuota = (n) =>
    new Date(inicio.getTime() + n * diasPeriodo * DIA);

  const desdeCuandoDebe = atrasadas > 0
    ? venceCuota((estado.cuotasPagadas || 0) + 1)
    : null;

  // La de hoy no cuenta como día de atraso: si debe tres cuotas, la más vieja
  // venció hace dos días, no hace tres.
  const diasAtraso = atrasadas > 1 ? (atrasadas - 1) * diasPeriodo : 0;

  const siguienteNum = Math.min(
    (estado.cuotasEsperadas || 0) + 1, estado.cuotasTotales || 0);
  const proximo = siguienteNum > 0 ? venceCuota(siguienteNum) : null;
  const diasParaProximo = proximo ? diaDeCorte(proximo) - hoyNum : null;

  return {
    estado,
    // «Le vence mañana» no se avisa en los diarios: mañana SIEMPRE vence una
    // cuota, así que todos los cobradiarios al día llenarían la lista de Por
    // cobrar y taparían a los que de verdad hay que ir a buscar. En semanal o
    // quincenal sí avisa, que ahí sí es una fecha señalada.
    situacion: atrasadas >= 2 ? 'atrasado'
             : atrasadas === 1 ? 'vence_hoy'
             : (diasPeriodo > 1 && diasParaProximo === 1) ? 'vence_manana'
             : 'al_dia',
    desdeCuandoDebe,
    diasAtraso,
    proximoCobro: proximo,
    diasParaProximo,
    // Lo que hay que cobrarle hoy: lo que trae atrasado, o la cuota si va al
    // día. Nunca más de lo que queda debiendo, que en la última cuota es menos.
    aCobrar: Math.min(
      estado.deudaTotal,
      Math.round(atrasadas > 0 ? estado.atrasoEnPlata : cuota)),
    cuotasAtrasadas: atrasadas,
  };
}

export function estadoDeCobro(prestamo, pagos = [], hoy = new Date()) {
  const referencia = hoy instanceof Date ? hoy : new Date(hoy);
  const ahora = hoyComoCorte(referencia);
  const hoyNum = diaDeHoy(referencia);
  const estado = estadoDelPrestamo(prestamo, pagos, hoy);

  // La bifurcación va arriba del todo, igual que en el motor: así ningún
  // préstamo de los de siempre puede caer por el camino nuevo.
  if (estado.modalidad === 'cuota_fija') {
    return cobroCuotaFija(prestamo, estado, referencia, hoyNum);
  }

  // Desde qué corte viene debiendo. Se recorre desde el más viejo y se pregunta,
  // en cada uno, si en ESA fecha ya estaba debiendo: el primero que diga que sí
  // es el día en que se atrasó.
  let desdeCuandoDebe = null;
  if (estado.interesPendiente > NADA) {
    for (const corte of cortesDePeriodo(prestamo, ahora)) {
      if (estadoDelPrestamo(prestamo, pagos, corte).interesPendiente > NADA) {
        desdeCuandoDebe = corte;
        break;
      }
    }
  }

  const diasAtraso = desdeCuandoDebe ? hoyNum - diaDeCorte(desdeCuandoDebe) : 0;

  // El próximo cobro que todavía no ha llegado. Si hoy es día de corte y ya lo
  // pagó, el próximo es el del mes que viene; si no lo ha pagado, el que cuenta
  // es el de hoy y sale por la vía del atraso.
  const proximo = siguienteCorte(prestamo, ahora);
  const diasParaProximo = proximo ? diaDeCorte(proximo) - hoyNum : null;

  return {
    estado,
    // 'atrasado' | 'vence_hoy' | 'vence_manana' | 'al_dia'
    //
    // Lo que decide si está atrasado es DEBER, no que hayan pasado días: a
    // quien le vence hoy y no ha pagado ya hay que cobrarle, aunque lleve cero
    // días. Mirando solo los días se colaba como «al día».
    situacion: desdeCuandoDebe
      ? (diasAtraso > 0 ? 'atrasado' : 'vence_hoy')
      : (diasParaProximo === 1 ? 'vence_manana' : 'al_dia'),
    desdeCuandoDebe,
    diasAtraso,
    proximoCobro: proximo,
    diasParaProximo,
    // Lo que hay que cobrarle: el interés que debe, o el del próximo periodo si
    // está al día.
    aCobrar: Math.round(
      desdeCuandoDebe ? estado.interesPendiente : estado.interesPorCobro),
  };
}

// La lista que pidió el cliente: quién vence mañana, quién vence hoy y quién ya
// se pasó. Ordenada por urgencia, que es el orden en que se llama por teléfono.
//
// `dias_aviso` es con cuánta anticipación se quiere ver. Uno por defecto: es lo
// que se pidió, y avisar con más días llena la lista de gente que todavía no
// debe nada.
export function porCobrar(prestamos = [], diasAviso = 1, hoy = new Date()) {
  const filas = prestamos.map(p => {
    const c = estadoDeCobro(p, p.pagos || [], hoy);
    return { ...p, cobro: c };
  });

  // Los que vencen hoy van con los atrasados: hay que cobrarles hoy, que es lo
  // que la lista tiene que contestar.
  const atrasados = filas
    .filter(f => f.cobro.situacion === 'atrasado' || f.cobro.situacion === 'vence_hoy')
    .sort((a, b) => b.cobro.diasAtraso - a.cobro.diasAtraso);

  const proximos = filas
    .filter(f => f.cobro.situacion !== 'atrasado' && f.cobro.situacion !== 'vence_hoy'
      && f.cobro.diasParaProximo !== null
      && f.cobro.diasParaProximo <= diasAviso)
    .sort((a, b) => a.cobro.diasParaProximo - b.cobro.diasParaProximo);

  return {
    atrasados,
    proximos,
    total: atrasados.length + proximos.length,
    montoAtrasado: atrasados.reduce((s, f) => s + f.cobro.aCobrar, 0),
    montoProximo: proximos.reduce((s, f) => s + f.cobro.aCobrar, 0),
  };
}
